import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../lib/axios';
import { useToast } from '../hooks/use-toast';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { UserPlus, Lock, User, Mail, Loader2, Eye, EyeOff, ShieldCheck, ArrowRight } from 'lucide-react';

// --- PROFESSIONAL LOGO IMPORT ---
import logoImg from '../uploads/logo.png';

export default function Register() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await api.post('/auth/register', { 
        username: username.trim(), 
        email: email.trim().toLowerCase(), 
        password 
      });
      
      if (response.data.success) {
        toast({
          title: 'Identity Verified',
          description: 'Your account has been provisioned. Please sign in.',
        });
        navigate('/login');
      }
    } catch (error) {
      toast({
        title: 'Registration Blocked',
        description: error.response?.data?.error || 'Validation failed. Try a different username/email.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4 selection:bg-indigo-100">
      
      {/* Background Decorative Blobs */}
      <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-10%] right-[-10%] w-[45%] h-[45%] bg-indigo-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[45%] h-[45%] bg-blue-100/40 rounded-full blur-3xl" />
      </div>

      <Card className="w-full max-w-md border-none shadow-[0_20px_50px_rgba(79,_70,_229,_0.1)] bg-white/80 backdrop-blur-xl z-10 rounded-3xl overflow-hidden ring-1 ring-white">
        
        {/* Animated Progress Bar during Load */}
        {loading && <div className="h-1 bg-indigo-600 animate-pulse absolute top-0 left-0 w-full" />}

        <CardHeader className="space-y-2 text-center pb-6 pt-10">
          <div className="mx-auto mb-4 group cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src={logoImg} 
              alt="Whatsappbot" 
              className="w-16 h-16 rounded-2xl object-contain shadow-lg transform transition-transform group-hover:scale-105 duration-300"
              onError={(e) => {
                e.target.src = 'https://placehold.co/200x200?text=Logo';
              }}
            />
          </div>
          <CardTitle className="text-3xl font-black tracking-tight text-slate-900">Create ID</CardTitle>
          <CardDescription className="text-slate-500 font-medium">
            Join the automated WhatsApp ecosystem
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Username */}
            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">Username</label>
              <div className="relative group">
                <User className="absolute left-4 top-3.5 h-5 w-5 text-slate-400 group-focus-within:text-indigo-600 transition-colors" />
                <Input 
                  className="pl-12 h-12 border-slate-200 focus:ring-indigo-500 bg-white/50 focus:bg-white transition-all rounded-2xl font-medium"
                  placeholder="johndoe_dev"
                  type="text" 
                  value={username} 
                  onChange={(e) => setUsername(e.target.value)} 
                  required 
                />
              </div>
            </div>

            {/* Email Address */}
            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">Work Email</label>
              <div className="relative group">
                <Mail className="absolute left-4 top-3.5 h-5 w-5 text-slate-400 group-focus-within:text-indigo-600 transition-colors" />
                <Input 
                  className="pl-12 h-12 border-slate-200 focus:ring-indigo-500 bg-white/50 focus:bg-white transition-all rounded-2xl font-medium"
                  placeholder="name@company.com"
                  type="email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  required 
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">Security Key</label>
              <div className="relative group">
                <Lock className="absolute left-4 top-3.5 h-5 w-5 text-slate-400 group-focus-within:text-indigo-600 transition-colors" />
                <Input 
                  className="pl-12 pr-12 h-12 border-slate-200 focus:ring-indigo-500 bg-white/50 focus:bg-white transition-all rounded-2xl font-medium"
                  placeholder="••••••••"
                  type={showPassword ? "text" : "password"} 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  required 
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-3.5 text-slate-400 hover:text-indigo-600 focus:outline-none transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button 
              disabled={loading}
              type="submit" 
              className="w-full h-14 bg-indigo-600 hover:bg-indigo-700 text-white transition-all font-black text-lg mt-6 shadow-[0_10px_20px_rgba(79,_70,_229,_0.15)] hover:shadow-indigo-300 rounded-2xl flex items-center justify-center gap-2 group active:scale-[0.98]"
            >
              {loading ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                <>
                  <UserPlus className="w-5 h-5" />
                  Initialize Account
                  <ArrowRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </>
              )}
            </Button>
          </form>
        </CardContent>
        
        <CardFooter className="flex flex-col items-center gap-4 border-t border-slate-50 pt-8 pb-10">
          <p className="text-sm font-medium text-slate-500">
            Already have a workspace?{' '}
            <Link to="/login" className="text-indigo-600 font-black hover:text-indigo-800 transition-colors">
              Sign In
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}